// HTTP client for the LiveVane PHP API.
//
// Design notes:
//   - Bearer token is pulled DIRECTLY from the auth store (not via a React
//     hook), so non-component callers (mutations, side effects, query fns)
//     can use the client without React context.
//   - Protected 401s preserve refreshable sessions for foreground recovery.
//     Definitively rejected refresh credentials clear the store, dispatch one
//     unauthorized event, and fall back to a hard redirect when needed.
//   - The PHP layer historically encodes array form fields as `name[]`
//     (PHP $_POST array convention). `toFormDataPhp` walks an object and
//     emits FormData honoring that convention so callers can pass plain
//     JS objects and still hit endpoints that expect repeated keys.
//   - Error envelope: PHP returns either `{ error, code? }` (per task brief)
//     or `{ success: false, message }` (legacy admin endpoints). We accept
//     both and surface a single `ApiError` with status/code/message.
//
// SECURITY:
//   - The bearer token is NEVER logged, NEVER thrown in error messages,
//     and NEVER stored in error stacks. Authorization header construction
//     is the only code path that touches the raw token string.
import {
  resolveAccessExpiry,
  useAuthStore,
  type AuthSession,
} from '@/stores/auth-store'
import type { LoginResponse } from '@/lib/types'

/** Custom event dispatched on 401. Router root listens and navigates. */
export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized'

/** Base URL for the API. Production: same-origin /api. Dev: env override. */
const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() || '/api'

/** Thrown for any non-2xx response. Carries status, optional code, message. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

const REFRESH_EARLY_MS = 2 * 60 * 1000
const REFRESH_TIMEOUT_MS = 15 * 1000
const REFRESH_LOCK_NAME = 'livevane.auth.refresh'
const REFRESH_LOCK_TTL_MS = REFRESH_TIMEOUT_MS + 10 * 1000

let unauthorizedInFlight = false
let refreshInFlight: Promise<boolean> | null = null
let refreshRequiredSessionId: string | null = null

function dispatchUnauthorizedEvent(): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT))
    }
  } catch {
    // Some environments restrict CustomEvent — non-fatal.
  }
}

function handleUnauthorized(): void {
  if (unauthorizedInFlight) return
  unauthorizedInFlight = true
  refreshRequiredSessionId = null

  try {
    useAuthStore.getState().clear()
  } catch {
    // Store hydration races are non-fatal.
  }

  if (typeof window === 'undefined') {
    unauthorizedInFlight = false
    return
  }

  const startPath = window.location.pathname
  dispatchUnauthorizedEvent()

  window.setTimeout(() => {
    try {
      if (
        window.location.pathname === startPath &&
        !window.location.pathname.startsWith('/login')
      ) {
        window.location.assign('/login')
      }
    } finally {
      unauthorizedInFlight = false
    }
  }, 50)
}

// ---- Body helpers --------------------------------------------------------

type RequestBody = unknown

export type RequestOpts = {
  signal?: AbortSignal
  headers?: Record<string, string>
  /** If true, skip the global 401 handler (e.g. for login attempts). */
  skipAuthRedirect?: boolean
  /** Override base URL for this call (rarely needed). */
  baseUrl?: string
  /** Internal guard: a failed protected request is replayed at most once. */
  authRetry?: boolean
  /** Internal: 'blob' returns the raw response body (binary assets). Defaults to JSON parsing. */
  responseType?: 'json' | 'blob'
}

function joinUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const b = base.replace(/\/+$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return `${b}${p}`
}

function isFormData(v: unknown): v is FormData {
  return typeof FormData !== 'undefined' && v instanceof FormData
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false
  const proto: unknown = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

/**
 * Walk a plain object and emit FormData using the PHP $_POST array
 * convention:
 *   - arrays become repeated `key[]` entries  → ids[]=1&ids[]=2
 *   - nested objects become bracketed paths   → meta[locale]=en
 *   - Blob / File values pass through as-is (supports file uploads)
 *   - null / undefined values are skipped (keeps optional fields out)
 *   - booleans serialize as '1' / '0' (PHP truthiness)
 *
 * Top-level callers can pre-suffix keys (`foo[]`) and the walker will
 * detect that and not double-suffix.
 */
export function toFormDataPhp(input: Record<string, unknown>): FormData {
  const fd = new FormData()

  const append = (key: string, value: unknown): void => {
    if (value === undefined || value === null) {
      return
    }
    if (Array.isArray(value)) {
      const arrayKey = key.endsWith('[]') ? key : `${key}[]`
      for (const item of value) {
        append(arrayKey, item)
      }
      return
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      fd.append(key, value)
      return
    }
    if (isPlainObject(value)) {
      for (const [k, v] of Object.entries(value)) {
        append(`${key}[${k}]`, v)
      }
      return
    }
    if (typeof value === 'boolean') {
      fd.append(key, value ? '1' : '0')
      return
    }
    fd.append(key, String(value))
  }

  for (const [k, v] of Object.entries(input)) {
    append(k, v)
  }
  return fd
}

/**
 * Append a `view_as=<id>` query string for super-admin impersonation.
 *
 * The legacy admin (www/livevane.com/htdocs/admin/index.html ~3720)
 * exposes a `scopedApiPath(path)` helper that decorates the path with
 * the super-admin's currently-impersonated target user id. This mirror
 * gives the SPA the same affordance — callers pass the current
 * `viewAsUserId` (read from local screen state) and we append the param
 * without disturbing any existing query string.
 *
 * Returns the path unchanged when `viewAsUserId` is null/undefined or
 * blank, so callers can safely thread an optional state value through
 * without branching on every site.
 *
 * Consumer of record: `apps/web/src/routes/admin/users.tsx` (Super
 * Admin Panel port). Future ported admin screens (Streams, Recordings,
 * Settings) are expected to reuse the same helper rather than
 * re-introducing the legacy `scopedApiPath` under a different name.
 * The helper is intentionally opt-in per call site so non-admin
 * surfaces never accidentally start sending `view_as` upstream.
 *
 * Example:
 *   scopedApiPath('/admin/users')               // '/admin/users'
 *   scopedApiPath('/admin/users', 42)           // '/admin/users?view_as=42'
 *   scopedApiPath('/admin/users?foo=1', 42)     // '/admin/users?foo=1&view_as=42'
 *   scopedApiPath('/admin/users', '   ')        // '/admin/users' (blank treated as null)
 */
export function scopedApiPath(
  path: string,
  viewAsUserId?: string | number | null,
): string {
  if (
    viewAsUserId === undefined ||
    viewAsUserId === null ||
    String(viewAsUserId).trim() === ''
  ) {
    return path
  }
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}view_as=${encodeURIComponent(String(viewAsUserId))}`
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function extractError(
  data: unknown,
  fallback: string,
): { message: string; code: string | undefined } {
  let message = fallback
  let code: string | undefined
  if (data && typeof data === 'object') {
    const obj = data as {
      error?: unknown
      message?: unknown
      code?: unknown
    }
    if (typeof obj.error === 'string' && obj.error.length > 0) {
      message = obj.error
    } else if (typeof obj.message === 'string' && obj.message.length > 0) {
      message = obj.message
    }
    if (typeof obj.code === 'string' && obj.code.length > 0) {
      code = obj.code
    }
  }
  return { message, code }
}

// ---- Core request --------------------------------------------------------


function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function rehydrateAuthStore(): Promise<void> {
  try {
    await useAuthStore.persist.rehydrate()
  } catch {
    // In-memory fallback remains authoritative when storage is unavailable.
  }
}

type LockManagerLike = {
  request: <T>(name: string, callback: () => Promise<T>) => Promise<T>
}

// IndexedDB read/write transactions serialize lease acquisition across tabs.
// localStorage read-modify-write is not an atomic cross-tab lock.
async function withStorageLock(callback: () => Promise<boolean>): Promise<boolean> {
  let db: IDBDatabase | undefined
  const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  try {
    db = await new Promise<IDBDatabase>((resolve, reject) => {
      const open = indexedDB.open('livevane-auth-locks', 1)
      open.onupgradeneeded = () => { open.result.createObjectStore('locks') }
      open.onsuccess = () => resolve(open.result)
      open.onerror = () => reject(open.error)
      open.onblocked = () => reject(new Error('Auth lock database blocked'))
    })
    const lease = (release = false): Promise<boolean> => new Promise((resolve, reject) => {
      const transaction = db!.transaction('locks', 'readwrite')
      const locks = transaction.objectStore('locks')
      const read = locks.get(REFRESH_LOCK_NAME)
      let acquired = false
      read.onsuccess = () => {
        const current = read.result as { owner: string; expiresAt: number } | undefined
        if (release) {
          if (current?.owner === owner) locks.delete(REFRESH_LOCK_NAME)
        } else if (!current || current.expiresAt <= Date.now()) {
          locks.put({ owner, expiresAt: Date.now() + REFRESH_LOCK_TTL_MS }, REFRESH_LOCK_NAME)
          acquired = true
        }
      }
      transaction.oncomplete = () => resolve(acquired)
      transaction.onabort = () => reject(transaction.error)
      transaction.onerror = () => reject(transaction.error)
    })
    while (!(await lease())) await delay(100)
    try {
      return await callback()
    } finally {
      await lease(true)
    }
  } catch {
    // Without a reliable shared lock, retain credentials for a later attempt.
    return false
  } finally {
    db?.close()
  }
}

async function withRefreshLock(
  callback: () => Promise<boolean>,
): Promise<boolean> {
  if (typeof navigator !== 'undefined') {
    const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks
    if (locks) return locks.request(REFRESH_LOCK_NAME, callback)
  }
  if (typeof window !== 'undefined') return withStorageLock(callback)
  return callback()
}

function isDefinitiveRefreshError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.status === 400 || error.status === 401 || error.status === 403)
  )
}

async function bestEffortRevoke(refreshToken: string | null): Promise<void> {
  if (!refreshToken) return
  try {
    await request<unknown>(
      'POST',
      '/auth/',
      { action: 'logout', refresh_token: refreshToken },
      { skipAuthRedirect: true },
    )
  } catch {
    // Logout remains local when the network or server is unavailable.
  }
}

async function performRefresh(expected: AuthSession): Promise<boolean> {
  return withRefreshLock(async () => {
    // The lock may have been held by another tab. Rehydrate before deciding
    // whether this refresh token is still current.
    await rehydrateAuthStore()
    let current = useAuthStore.getState()
    if (
      current.sessionId !== expected.sessionId ||
      current.refreshToken !== expected.refreshToken
    ) {
      refreshRequiredSessionId = null
      return current.token !== null
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
    try {
      const response = await request<LoginResponse>(
        'POST',
        '/auth/',
        { action: 'refresh', refresh_token: expected.refreshToken },
        { skipAuthRedirect: true, signal: controller.signal },
      )

      if (
        !response ||
        response.success !== true ||
        !response.access_token ||
        !response.refresh_token
      ) {
        return false
      }

      // Re-read shared storage immediately before publishing the rotated pair.
      // This prevents a logout or a new login in any tab from being overwritten
      // by an older in-flight response.
      await rehydrateAuthStore()
      current = useAuthStore.getState()
      const updated = current.rotateSession(
        expected.sessionId,
        expected.refreshToken,
        {
          token: response.access_token,
          refreshToken: response.refresh_token,
          expiresAt: resolveAccessExpiry(response.expires_in, response.expires_at),
          user: response.user ?? expected.user,
        },
      )
      if (!updated) {
        void bestEffortRevoke(response.refresh_token)
        return useAuthStore.getState().token !== null
      }

      refreshRequiredSessionId = null
      return true
    } catch (error) {
      await rehydrateAuthStore()
      current = useAuthStore.getState()
      if (
        isDefinitiveRefreshError(error) &&
        current.sessionId === expected.sessionId &&
        current.refreshToken === expected.refreshToken
      ) {
        handleUnauthorized()
      }
      return false
    } finally {
      clearTimeout(timeout)
    }
  })
}

/** Renew only after foreground activity, and only near/after expiry or a 401. */
export async function refreshAuthSession(): Promise<boolean> {
  const state = useAuthStore.getState()
  if (
    !state.token ||
    !state.refreshToken ||
    !state.user ||
    !state.sessionId
  ) {
    return false
  }

  const needsRefresh =
    refreshRequiredSessionId === state.sessionId ||
    (state.expiresAt !== null && state.expiresAt - Date.now() <= REFRESH_EARLY_MS)
  if (!needsRefresh) return true
  if (refreshInFlight) return refreshInFlight

  const expected: AuthSession = {
    token: state.token,
    refreshToken: state.refreshToken,
    expiresAt: state.expiresAt,
    user: state.user,
    sessionId: state.sessionId,
  }
  const pending = performRefresh(expected)
  refreshInFlight = pending
  try {
    return await pending
  } finally {
    if (refreshInFlight === pending) refreshInFlight = null
  }
}

/** Revoke the captured device credential without delaying local sign-out. */
export function revokeAuthSession(refreshToken: string | null): void {
  void bestEffortRevoke(refreshToken)
}
async function request<T>(
  method: string,
  path: string,
  body: RequestBody,
  opts: RequestOpts,
): Promise<T> {
  if (!opts.skipAuthRedirect && refreshInFlight) {
    await refreshInFlight
  }

  const base = opts.baseUrl ?? BASE_URL
  const url = joinUrl(base, path)
  const headers: Record<string, string> = {
    Accept: opts.responseType === 'blob' ? '*/*' : 'application/json',
    ...(opts.headers ?? {}),
  }

  let payload: BodyInit | undefined
  if (body !== undefined && body !== null) {
    if (isFormData(body)) {
      // Browser sets multipart boundary header — do NOT set Content-Type.
      payload = body
    } else if (typeof body === 'string') {
      payload = body
      if (!headers['Content-Type']) {
        headers['Content-Type'] = 'text/plain;charset=UTF-8'
      }
    } else {
      payload = JSON.stringify(body)
      if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json'
      }
    }
  }

  // Attach bearer token (direct store read — works outside React).
  const authAtRequest = useAuthStore.getState()
  const token = authAtRequest.token
  const sessionIdAtRequest = authAtRequest.sessionId
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let res: Response
  try {
    res = await fetch(url, {
      method,
      headers,
      body: payload,
      signal: opts.signal,
      credentials: 'same-origin',
    })
  } catch (err) {
    // Network-layer failure — wrap as ApiError so callers have one shape.
    // Note: we intentionally do NOT include URL details that could leak
    // the bearer token via error stacks; status 0 signals "no response".
    const msg = err instanceof Error ? err.message : 'Network request failed.'
    throw new ApiError(msg, 0)
  }

  if (res.status === 401 && !opts.skipAuthRedirect) {
    // A rotation may already have completed in this or another tab. Rehydrate
    // shared credentials before deciding whether this response is stale.
    await rehydrateAuthStore()
    let latest = useAuthStore.getState()

    // Never replay work from an old session under a newly authenticated user.
    // Session ids remain stable across token rotation, but change on login.
    if (latest.sessionId === sessionIdAtRequest) {
      if (!opts.authRetry && latest.token && latest.token !== token) {
        return request<T>(method, path, body, { ...opts, authRetry: true })
      }

      if (!opts.authRetry && refreshInFlight && latest.refreshToken) {
        await refreshInFlight
        await rehydrateAuthStore()
        latest = useAuthStore.getState()
        if (
          latest.sessionId === sessionIdAtRequest &&
          latest.token &&
          latest.token !== token
        ) {
          return request<T>(method, path, body, { ...opts, authRetry: true })
        }
      }

      if (latest.sessionId === sessionIdAtRequest) {
        if (latest.refreshToken && latest.sessionId) {
          // A background poll must not renew an idle session. Remember the 401
          // so the next trusted foreground action can recover it instead.
          refreshRequiredSessionId = latest.sessionId
        } else {
          // Legacy access-only sessions retain their old behavior:
          // authentication is required once the token is actually rejected.
          handleUnauthorized()
        }
      }
    }
  }

  if (opts.responseType === 'blob' && res.ok) {
    return (await res.blob()) as T
  }

  const data = await parseJsonSafe(res)

  if (!res.ok) {
    const { message, code } = extractError(data, `Request failed (${res.status}).`)
    throw new ApiError(message, res.status, code)
  }

  return data as T
}

// ---- Public method helpers ----------------------------------------------

export const apiClient = {
  get: <T>(path: string, opts: RequestOpts = {}): Promise<T> =>
    request<T>('GET', path, undefined, opts),

  post: <T>(path: string, body?: RequestBody, opts: RequestOpts = {}): Promise<T> =>
    request<T>('POST', path, body, opts),

  put: <T>(path: string, body?: RequestBody, opts: RequestOpts = {}): Promise<T> =>
    request<T>('PUT', path, body, opts),

  patch: <T>(path: string, body?: RequestBody, opts: RequestOpts = {}): Promise<T> =>
    request<T>('PATCH', path, body, opts),

  del: <T>(path: string, opts: RequestOpts = {}): Promise<T> =>
    request<T>('DELETE', path, undefined, opts),

  /** Authenticated binary GET (Bearer token, 401 handling) returning the response body as a Blob. */
  getBlob: (path: string, opts: RequestOpts = {}): Promise<Blob> =>
    request<Blob>('GET', path, undefined, { ...opts, responseType: 'blob' }),
}

export type ApiClient = typeof apiClient
